import React from 'react';
import { BarChart2, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров инвестиций
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  portfolioTypes
}) => {
  // Предопределенные суммы инвестиций для быстрого выбора
  const predefinedAmounts = [
    { value: '30000', label: '30 тыс.' },
    { value: '100000', label: '100 тыс.' },
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' }
  ];
  
  // Предопределенные сроки инвестирования для быстрого выбора
  const predefinedTerms = [
    { value: '1', label: '1 год' },
    { value: '3', label: '3 года' },
    { value: '10', label: '10 лет' },
    { value: '20', label: '20 лет' }
  ];
  
  // Предопределенные суммы ежемесячных взносов
  const predefinedMonthlyContributions = [
    { value: '0', label: 'Нет' },
    { value: '5000', label: '5 тыс.' },
    { value: '10000', label: '10 тыс.' },
    { value: '30000', label: '30 тыс.' }
  ];
  
  // Функция выбора типа портфеля
  const handlePortfolioTypeSelection = (typeId) => {
    const selectedType = portfolioTypes.find(type => type.id === typeId);
    if (!selectedType) return;
    
    setFormData(prev => ({
      ...prev,
      portfolioType: typeId,
      assetAllocation: { ...selectedType.allocation }
    }));
  };
  
  // Функция расчета
  const calculateInvestments = () => {
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Начальная сумма инвестиций */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="initialInvestmentSlider" className="text-gray-600 dark:text-gray-400">Начальная сумма</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(parseFloat(formData.initialInvestment) || 0, 0)}
            </span>
          </div>
          <input
            type="range"
            id="initialInvestmentSlider"
            min="10000"
            max="3000000"
            step="10000"
            value={formData.initialInvestment}
            onChange={(e) => setFormData(prev => ({ ...prev, initialInvestment: e.target.value }))}
            aria-label="Начальная сумма инвестиций"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, initialInvestment: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.initialInvestment === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок инвестирования */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="investmentTermSlider" className="text-gray-600 dark:text-gray-400">Срок инвестирования</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.investmentTerm} {pluralize(parseFloat(formData.investmentTerm) || 0, 'год', 'года', 'лет')} 
              ({Math.round((parseFloat(formData.investmentTerm) || 0) * 12)} мес.)
            </span>
          </div>
          <input
            type="range"
            id="investmentTermSlider"
            min="1"
            max="30"
            step="1"
            value={formData.investmentTerm}
            onChange={(e) => setFormData(prev => ({ ...prev, investmentTerm: e.target.value }))}
            aria-label="Срок инвестирования (лет)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedTerms.map(term => (
              <button
                key={term.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, investmentTerm: term.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.investmentTerm === term.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {term.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Ежемесячный взнос */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="monthlyContributionSlider" className="text-gray-600 dark:text-gray-400">Ежемесячный взнос</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(parseFloat(formData.monthlyContribution) || 0, 0)}
            </span>
          </div>
          <input
            type="range"
            id="monthlyContributionSlider"
            min="0"
            max="100000"
            step="1000"
            value={formData.monthlyContribution}
            onChange={(e) => setFormData(prev => ({ ...prev, monthlyContribution: e.target.value }))}
            aria-label="Ежемесячный взнос"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные ежемесячные взносы */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedMonthlyContributions.map(contribution => (
              <button
                key={contribution.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, monthlyContribution: contribution.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.monthlyContribution === contribution.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {contribution.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор типа портфеля */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип портфеля</label>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {portfolioTypes.filter(type => type.id !== 'custom').map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handlePortfolioTypeSelection(type.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.portfolioType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">{type.name}</div>
                <div className="text-xs mt-1 flex justify-between">
                  <span>Риск: {type.riskLevel}</span>
                  <span>~{type.expectedReturn}% годовых</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateInvestments}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать доходность
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;